import { useState } from 'react';
import { changeProductStatus, getAllProducts, getInsurerProfile, insurerSubscribeToProduct } from '../../../../api/requests';
import Page from '../../../../components/PageWrapper';
import Loader from '../../../../components/Loader';
import Button from '../../../../components/form-control/Button';
import { PlusIcon } from '@heroicons/react/24/solid';
import useModal from '../../../../hooks/modalHook';
import useUserAccount from '../../../../hooks/userAccountHook';
import PopOnMe from '../../../../components/elements/poponme';
import { BsThreeDotsVertical } from 'react-icons/bs';
import clsx from 'clsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IProduct } from '../../../../resources/interfaces';
import { User } from '../../../../models/User';
import { Ban, CheckCircle } from 'lucide-react';
import ActionConfirmation from '../../../../components/elements/ActionConfirmation';
import useProduct from '../../../../hooks/productHook';
import { getFormattedDate, showToast } from '../../../../utils/miscFunctions';

export interface ChangeProductStatusProps {
  productId: string;
  status: boolean;
}

interface IProps {
  isSettings?: boolean;
}

const index = ({isSettings=false}: IProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<IProduct[]>([]);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { user, updateInsurerInState, getInsurer } = useUserAccount();
  const {addProduct, ProductModal} = useProduct();
  const insurerId = Number(user.company?.id);
  const queryClient = useQueryClient();

  const insurer = getInsurer();

  useQuery({
    queryKey: ['getProducts', products],
    queryFn: () => getProducts(),
  });

  const getProducts = async () => {
    try {
      const response: any = await getAllProducts();
      setProducts(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };


  const getInsurerProfileDetails = () => {
    getInsurerProfile().then((response: any) => {
      updateInsurerInState(response);
    });

  }

  const handleSubscribeProduct = async (productId: string, add: boolean) => {
    try {
      if (!insurerId) return;
      const response: any = await insurerSubscribeToProduct(insurerId, add, [productId]);
      showToast.success(response.message ?? 'Product subscribed successfully');
      queryClient.invalidateQueries({ queryKey: ['getProducts'] });
      getInsurerProfileDetails()


    } catch (error: any) {
      showToast.error(error.description);
    } finally {
      setLoading(false);
    }
  };

  const productActions = (product: IProduct) => {
    const actions = [];

    const productIds = products.map((p: any) => p.id);

    const insurerSubscribedProducts = insurer.products.filter((p: any) =>
      productIds.includes(p.id)
    );

    const isSubscribed = insurerSubscribedProducts.some(
      (p: any) => p.id === product.id
    );

    if (user.isOwner()) {
      // if (user.isSuperAdmin()) {
      actions.push({
        label: product.active ? 'Deactivate' : 'Activate',
        callback: () => toggleProductStatus(product.id, product.active),
        icon: product.active ? <Ban className="h-4 w-4" /> : <CheckCircle className='h-4 w-4' />
      });
    }

    if (user.isInsurerAdmin() || user.isInsurerOwner()) {
      // if (user.isInsurerAdmin() || user.isInsurerSuperAdmin()) {
      actions.push({
        label: isSubscribed ? 'Unsubscribe' : 'Subscribe',
        callback: () => handleSubscribeProduct(product.id, isSubscribed ? false : true),
        icon: isSubscribed ? <Ban className="h-4 w-4" /> : <CheckCircle className='h-4 w-4' />
      });
    }

    return actions;
  };

  const toggleProductStatus = (productId: string, status: boolean) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          type={status ? "warning" : "success"}
          onClose={closeModal}
          action={status ? "Deactivate" : "Activate"}
          onConfirm={() => {
            changeProductStatus(productId, !status)
              .then((response: any) => {
                showToast.success(response.message ?? 'Product status changed successfully');
                queryClient.invalidateQueries({ queryKey: ['getProducts'] });
              })
              .catch((error: any) => {
                showToast.error(error.description);
              }).finally(() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['getAllCompanyStaff'] }) })
          }}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      title: ''
    });
  };

  return (
    <Page title="Products">
      <div className="min-h-full bg-white dark:bg-darkBg w-full p-5">
        {loading ? (
      
              <Loader message="Fetching products. Please wait..." brollyLoaderFixed />
         
        ) : (
          <>
            {!isSettings && <div className="flex items-center justify-between ">

              {user.isOwner() && (
                // {user.isSuperAdmin() && (
                <Button
                  label="Add Product"
                  className="py-2 px-2"
                  bgColor="primary"
                  icon={<PlusIcon className="h-4 w-4 mr-2" />}
                  onClick={addProduct}
                  curved
                />
              )}
            </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-6">
              {products.map((product: any, index: number) => {
                return (
                  <ProductCard
                    key={index}
                    product={product}
                    productActions={productActions(product)}
                    user={user}
                  />
                );
              })}
            </div>

          </>
        )}
        <ModalComponent />
        <ProductModal />
      </div>
    </Page>
  );
};

const ProductCard = ({ product, productActions, user }: ProductCardProps) => {
  const { getInsurer } = useUserAccount();
  const insurer = getInsurer();

  // Check if the current product is in the insurer's subscribed products
  const isSubscribed = insurer?.products?.some((p: any) => p.id === product.id) || false;

  return (
    <div className="relative p-6 bg-white dark:bg-darkBg shadow-md rounded-lg transition duration-300 hover:shadow-lg border border-gray-100 dark:border-darkBg-border overflow-hidden">
      {/* Subscription status ribbon - Moved to left side */}
      {user.isInsurer() && (
        <div
          className={`absolute top-4 -left-8 w-32 transform -rotate-45 text-center text-xs font-medium py-1 ${isSubscribed
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800 text-2xs'
            }`}
        >
          {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-darkBg-subText mb-4">
        <span className="text-xs">
          {`Last modified: ${product.updatedOn ? getFormattedDate({ date: product.updatedOn, showTime: true }) : '-'}`}
        </span>

        <div className="relative">
          {productActions.length > 0 && (
            <PopOnMe
              actions={productActions}
              popOptionClasses="!h-[40px] !min-w-[160px]"
              tippyWrapperStyles={{
                minWidth: '160px',
                padding: '8px 0',
                zIndex: 50
              }}
            >
              <div className="cursor-pointer hover:text-gray-700 dark:hover:text-darkBg-mainText p-1 rounded-full hover:bg-gray-100 dark:hover:bg-darkBg-border">
                <BsThreeDotsVertical className="h-5 w-5" />
              </div>
            </PopOnMe>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{product.name}</h4>
          </div>

          <div className="flex items-center bg-gray-50 dark:bg-darkBg-border px-2 py-1 rounded-full">
            <div
              className={clsx(
                'h-2 w-2 rounded-full mr-2',
                product.active ? 'bg-green-500' : 'bg-red-500'
              )}
            />
            <span className="text-xs font-medium text-gray-700 dark:text-darkBg-mainText">
              {product.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {product.description && (
          <p className="text-gray-600 dark:text-darkBg-subText text-sm line-clamp-2">{product.description}</p>
        )}
      </div>
    </div>
  );
};

type ProductCardProps = {
  product: IProduct;
  productActions: any;
  user: User;
};

export default index;
